import React from 'react';
import { Card } from '@/components/ui/Card';
import { Search, X, MapPin, Maximize2, Hexagon, RotateCcw, Undo2, Trash2 } from 'lucide-react';
import { cn } from '@/utils/cn';

export function ZoneBoundaryMap() {
  return (
    <div className="space-y-6">
      {/* Ranh giới khu vực */}
      <Card className="p-6 border-slate-100 flex flex-col h-[600px]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-normal text-slate-900">Ranh giới khu vực</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Vẽ và xác định ranh giới khu vực trên bản đồ</p>
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors sm:text-sm text-xs border border-slate-200">
            <X className="w-3.5 h-3.5" />
            Xóa vùng vẽ
          </button>
        </div>

        <div className="relative flex-1 rounded-md overflow-hidden border border-slate-200 bg-slate-100">
          {/* Map Image Placeholder (In real app, this is Leaflet/Google Maps) */}
          <div className="absolute inset-0 bg-[url('https://maps.googleapis.com/maps/api/staticmap?center=10.823456,106.629123&zoom=17&size=800x600&maptype=satellite')] bg-cover bg-center" />
          
          {/* Overlay Polygon Simulation */}
          <div className="absolute inset-0 bg-emerald-500/20" style={{ clipPath: 'polygon(30% 20%, 80% 30%, 90% 70%, 70% 90%, 20% 80%)' }}>
            <div className="w-full h-full border-2 border-emerald-400" />
          </div>

          {/* Map Controls */}
          <div className="absolute top-4 left-4 right-4 flex gap-4">
            <div className="relative flex-1 max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Tìm địa điểm..."
                className="w-full pl-9 pr-3 py-2 bg-white rounded-md sm:text-sm text-xs outline-none border border-slate-200"
              />
            </div>
          </div>

          {/* Drawing Tools - Left Side */}
          <div className="absolute top-16 left-4 bg-white rounded-md border border-slate-200 flex flex-col p-1 gap-1">
            <button className="p-2 rounded text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-colors">
              <MapPin className="w-4 h-4" />
            </button>
            <button className="p-2 rounded bg-emerald-50 text-emerald-600 transition-colors border border-emerald-100">
              <Hexagon className="w-4 h-4" />
            </button>
            <div className="h-px bg-slate-100 my-1 mx-2" />
            <button className="p-2 rounded text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-colors">
              <Undo2 className="w-4 h-4" />
            </button>
            <button className="p-2 rounded text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
            <div className="h-px bg-slate-100 my-1 mx-2" />
            <button className="p-2 rounded text-slate-600 hover:bg-slate-50 hover:text-rose-600 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Fullscreen Tool - Bottom Right */}
          <button className="absolute bottom-4 right-4 p-2 bg-white rounded-md border border-slate-200 text-slate-600 hover:text-emerald-600 transition-colors">
            <Maximize2 className="w-4 h-4" />
          </button>

          {/* Instruction Tooltip - Bottom Left */}
          <div className="absolute bottom-4 left-4 bg-white px-3 py-2 rounded-md border border-slate-200 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-600" />
            <span className="text-[11px] font-normal text-slate-700">Nhấp vào bản đồ để tạo điểm, nhấp điểm đầu tiên để hoàn tất</span>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Thông tin ranh giới */}
        <Card className="p-6 border-slate-100 space-y-6">
          <h3 className="text-sm font-normal text-slate-900">Thông tin ranh giới</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <div className="flex items-center gap-2 text-slate-500">
                <Hexagon className="w-4 h-4" />
                <span className="sm:text-sm text-xs font-normal">Diện tích</span>
              </div>
              <span className="sm:text-sm text-xs text-emerald-600 font-normal">12.450 m²</span>
            </div>
            
            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <div className="flex items-center gap-2 text-slate-500">
                <RotateCcw className="w-4 h-4" />
                <span className="sm:text-sm text-xs font-normal">Chu vi</span>
              </div>
              <span className="sm:text-sm text-xs text-emerald-600 font-normal">485 m</span>
            </div>

            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-4 h-4" />
                <span className="sm:text-sm text-xs font-normal">Số điểm</span>
              </div>
              <span className="sm:text-sm text-xs text-slate-900 font-normal">6 điểm</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-500">
                <Search className="w-4 h-4" />
                <span className="sm:text-sm text-xs font-normal">Tọa độ tâm</span>
              </div>
              <span className="sm:text-sm text-xs text-slate-900 font-normal">10.823456, 106.629123</span>
            </div>
          </div>
        </Card>

        {/* Xem trước */}
        <Card className="p-6 border-slate-100 flex flex-col items-center justify-center text-center space-y-4 min-h-[200px]">
          <h3 className="text-sm font-normal text-slate-900 self-start w-full text-left">Xem trước</h3>
          
          <div className="flex-1 flex flex-col items-center justify-center pt-2">
            <div className="relative w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <MapPin className="w-8 h-8 text-emerald-600 absolute -top-2" />
              <div className="w-8 h-2 bg-emerald-600/20 rounded-[100%] blur-[2px] absolute bottom-3" />
            </div>
            <p className="sm:text-sm text-xs text-slate-500 leading-relaxed max-w-[200px]">
              Khu vực sẽ hiển thị trên bản đồ và trong các danh sách thi công, bảo dưỡng liên quan.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
